import type { NexusReasonCode } from '@barmetvault/shared';
/**
 * Nexus Equipo — análisis REAL del backend (POST /work/nexus/organize-team).
 *
 * Contrato real (OrganizeTeamResponse):
 *   { date, weekStart, employees[], detections[], unassignedTasks[],
 *     reassignmentProposals: ReassignmentProposal[], note }
 * ReassignmentProposal:
 *   { taskId, taskCode, title, fromUserId, reason,
 *     candidates: [{ userId, email, displayName, weekLoadMinutes,
 *                    weekCapacityMinutes, memberOfProject, reasons[] }] }
 *
 * NEXUS NUNCA reasigna: todo es propuesta y exige confirmación humana.
 * RBAC: SUPERADMIN = ALLOW; EMPLOYEE = DENY; PROJECT_GUEST = DENY.
 */
import { useState } from 'react';
import { taskHierarchyLabel } from './tasks/taskTypes';
import { api } from '../utils/api';

interface Candidate {
  userId: string;
  email: string;
  displayName: string | null;
  weekLoadMinutes: number;
  weekCapacityMinutes: number;
  memberOfProject: boolean;
  reasons: string[];
  reasonCodes: NexusReasonCode[];
}

interface ReassignmentProposal {
  taskId: string;
  taskCode: string;
  title: string;
  fromUserId: string | null;
  reason: string;
  reasonCodes: NexusReasonCode[];
  candidates: Candidate[];
}

interface OrganizeTeamResponse {
  date: string;
  weekStart: string;
  employees: Array<{ userId: string; email: string; weekLoadMinutes: number; weekCapacityMinutes: number }>;
  detections: Array<{ code: string; scope: string; detail: string; reasonCodes: NexusReasonCode[] }>;
  unassignedTasks: Array<{ id: string; taskCode: string; title: string }>;
  reassignmentProposals: ReassignmentProposal[];
  note: string;
}

export function NexusTeamPanel({ userRole }: { userRole?: string } = {}) {
  void userRole; // El RBAC real lo aplica el backend (SUPERADMIN-only).
  const [data, setData] = useState<OrganizeTeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/work/nexus/organize-team', {});
      setData(res.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'No se pudo analizar el equipo');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nexus-team-panel" data-testid="nexus-team-panel">
      <h2>Nexus · Equipo</h2>
      <button data-testid="nexus-team-run" onClick={() => void run()} disabled={loading}>
        {loading ? 'Analizando equipo…' : 'Analizar equipo'}
      </button>

      {loading && <div data-testid="nexus-team-loading" role="status">Analizando equipo…</div>}
      {error && <div data-testid="nexus-team-error" role="alert">{error}</div>}

      {data && !loading && (
        <div data-testid="nexus-team-result">
          <section>
            <h3>Detecciones ({data.detections.length})</h3>
            {data.detections.length === 0 && <p data-testid="nexus-team-no-detections">Sin anomalías detectadas.</p>}
            <ul>
              {data.detections.map((d, i) => (
                <li key={`${d.code}-${i}`} data-testid="nexus-team-detection" data-code={d.code}>
                  [{d.code}] {d.detail}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3>Propuestas de reasignación ({data.reassignmentProposals.length})</h3>
            {data.reassignmentProposals.length === 0 && (
              <p data-testid="nexus-team-no-proposals">No hay propuestas de reasignación.</p>
            )}
            <ul>
              {data.reassignmentProposals.map((p) => (
                <li key={p.taskId} data-testid="nexus-team-proposal">
                  <div>
                    {taskHierarchyLabel({ taskCode: p.taskCode, title: p.title })}
                  </div>
                  <div data-testid="nexus-team-proposal-reason">Motivo: {p.reason}</div>
                  <ul>
                    {p.candidates.map((c) => (
                      <li key={c.userId} data-testid="nexus-team-candidate">
                        {c.displayName || c.email} · carga {c.weekLoadMinutes}/{c.weekCapacityMinutes} min
                        {c.memberOfProject ? ' · miembro del proyecto' : ''}
                        {c.reasons.length > 0 && ` · ${c.reasons.join(' · ')}`}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </section>

          {data.unassignedTasks.length > 0 && (
            <section>
              <h3>Tareas sin responsable ({data.unassignedTasks.length})</h3>
              <ul>
                {data.unassignedTasks.map((t) => (
                  <li key={t.id}>{taskHierarchyLabel({ taskCode: t.taskCode, title: t.title })}</li>
                ))}
              </ul>
            </section>
          )}

          <p className="hint" data-testid="nexus-team-note">{data.note}</p>
        </div>
      )}
    </div>
  );
}
